/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {ColDef} from 'ag-grid-community';
import classnames from 'classnames';
import {AuditRowMenuEditor} from '@ion/applications/src/pages/admin/users/AuditRowMenuEditor';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {audit, loadUsers} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {AlertFired, AuditRecord, Entity} from '@ion/core/src/types/entities';
import {DateRangeType} from '@ion/core/src/types/services';
import {idMap} from '@ion/core/src/utils/ObjectUtil';
import {useCurrentFileHistory} from '@ion/query/src/hooks/useCurrentFileHistory';
import {resolveDefinitions} from '../../grid/ColumnResolver';
import IonGrid from '../../grid/IonGrid';
import {
  getGridCellStyles,
  getGridDragStyles,
  getGridHeaderStyles
} from '../../grid/IonGridUtils';
import {GridResult} from '../../grid/PaginationControls';
import {PAGE_SIZES} from '../../grid/settings';
import {useMedia} from '../../hooks/useMedia';
import DragIcon from '../../icons/current/DragIcon';
import HoverDragIcon from '../../icons/current/HoverDragIcon';
import getGlobalProp from '../../props/GlobalPropsConfig';
import {toggleThemeStyle} from '../../theme/helpers';
import {DLSTheme} from '../../types/types';
import {formatUserDateTime} from '../../utils/ComponentUtils';
import {
  AttributesCellRenderer,
  CTCNewValuesRenderer,
  CTCOldValuesRenderer,
  ValuesRenderer
} from './AttributesCellRenderer';
import {
  AuditRecordType,
  AuditTransformOptions,
  DEFAULT_ROW_HEIGHT,
  DELIMITER,
  getUserName,
  mappingAuditRecords,
  postProcess
} from './AuditGridUtils';

// grid height above which scroll is enabled ...
export const MIN_GRID_HEIGHT = 450;

// maps an entity to the field on `item` used as the audit lookup key,
// defaulting to `id` when the entity isn't listed here ...
const AUDIT_KEY_FIELD_BY_ENTITY = {
  [Entity.application] : 'applicationKey',
  [Entity.vescout]     : 'esn'
};

export interface AuditGridOptions {
  showDifferences?: boolean;
  rowMenusEnabled?: boolean;
  showAuditVersion?: boolean;
  enableRangeSelection?: boolean;
  transformOptions?: AuditTransformOptions;
};

// defines the prop definitions for audit grid ...
export interface AuditGridProps extends AuditGridOptions {
  item: AlertFired,
  entity: Entity,
  onViewAuditChanges?: Function;
  enableDateFilter?: boolean;
  setHasFileHistory?: Function;
  selectedRange?: DateRangeType;
};

// defines the styles used by audit grid ...
export const useStyles = makeStyles((theme: DLSTheme) => {
  const getHeight = (gridHeight: number) => `${gridHeight}px !important`;
  return ({
    showDifferencesStyle : {
      '&&' : {
        height : '412px'
      },
      '&& .ag-center-cols-container' : {
        height   : '100% !important',
        overflow : 'hidden !important'
      },
      '&& .ag-center-cols-viewport' : {
        height : '363px !important'
      }
    },
    auditGrid : toggleThemeStyle({
      height                         : 'calc(100% - 70px) !important',
      [theme.breakpoints.down('sm')] : {
        height : 'calc(100% - 112px) !important'
      },
      '& .ag-center-cols-container' : {
        height   : '100% !important',
        overflow : 'hidden !important'
      },
      '& .ag-header' : {
        top                            : '-8px',
        [theme.breakpoints.down('sm')] : {
          top : '0px'
        }
      }
    }, {
      height                         : 'calc(100% - 70px) !important',
      [theme.breakpoints.down('sm')] : {
        height : 'calc(100% - 112px) !important'
      },
      '& .ag-center-cols-container' : {
        height   : '100% !important',
        overflow : 'hidden !important'
      },
      ...getGridHeaderStyles(),
      ...getGridDragStyles({
        dragIcon      : <DragIcon />,
        hoverDragIcon : <HoverDragIcon />
      }),
      ...getGridCellStyles()
    }),
    auditGridCustom : {
      '& .ag-center-cols-viewport' : {
        minHeight : getHeight
      },
      '& .ag-center-cols-container' : {
        overflowY : 'hidden !important'
      },
      '& .ag-body-vertical-scroll-container' : {
        height : getHeight
      },
      '& .ag-viewport' : {
        '& .ag-row' : {
          '& .ag-cell' : {
            display    : 'inline-block',
            lineHeight : '40px !important'
          }
        }
      }
    },
    // gives the Audit Log popup's pagination footer breathing room above it;
    // current theme only, and scoped to this grid alone ...
    paginationPadding : {
      paddingTop : toggleThemeStyle(undefined, '20px')
    }
  });
});

/**
 * @AuditGrid
 *
 * Grid to be rendered on the Admin page
 */
export const AuditGrid = ({
  item,
  entity,
  rowMenusEnabled = false,
  showDifferences = false,
  showAuditVersion = false,
  onViewAuditChanges,
  enableDateFilter = false,
  enableRangeSelection = false,
  setHasFileHistory,
  selectedRange,
  transformOptions : {includeEntity = false, transformRecords} = {}
}: AuditGridProps) => {
  const isMounted                   = useHasMounted();
  const [gridHeight, setGridHeight] = useState<number>(0);
  const classes                     = useStyles(gridHeight);
  const [gridItems, setGridItems]   = useState([]);
  const [gridApi, setGridApi]       = useState(null);
  const {isPhone}                   = useMedia();
  const {fileHistory}               = useCurrentFileHistory(entity === Entity.device ? item?.esn : undefined);

  /**
   * @private
   */
  function getGridHeight(height) {
    setGridHeight(height);
    return classnames(
      classes.auditGrid,
      classes.auditGridCustom,
      (showDifferences && classes.showDifferencesStyle)
    );
  };

  useEffect(() => {
    if (gridApi) {
      gridApi.hideOverlay();
      if (!gridItems.length) {
        gridApi.showNoRowsOverlay();
      }
    }
  }, [gridItems]);

  // Provides reference to the grid's api.
  function onGridReady(api) {
    setGridApi(api);
  };

  /**
   * @private
   */
  const loadItems = async function({page, pageSize, callback}) {
    let callbackResults  = null;
    let deviceFilesData  = [];
    const associateIndex = AUDIT_KEY_FIELD_BY_ENTITY[entity] || 'id';
    try {
      const {results: records = [], totalCount} = await audit({
        id           : item[associateIndex],
        pageId       : page + 1,
        customFilter : selectedRange,
        includeEntity,
        enableDateFilter,
        pageSize,
        entity
      });
      const results = transformRecords?.(records) ?? records;
      if (results?.length) {
        const userIds = results.map(({user}: AuditRecord) => entityId(user));
        const {users} = await loadUsers({pageSize: 100, filterOn: {id: userIds.toString()}});
        const userMap = idMap(users);
        results.forEach((record: AuditRecord) => {
          const user  = userMap[entityId(record.user)];
          record.user = getUserName(user);
        });
      }
      if (entity === 'device') {
        deviceFilesData = postProcess(fileHistory, selectedRange);
        setHasFileHistory(!!fileHistory.length);
      }
      const finalConcatinatedArray  = results.concat(deviceFilesData);
      const showOptions             = {showDifferences, showAuditVersion};
      const items                   = await mappingAuditRecords(finalConcatinatedArray, isPhone, showOptions);
      callbackResults               = {items, totalCount};
      callback(callbackResults);
      setGridItems(items);
    } catch (err) {
      callbackResults = {items: [], totalCount: 0};
      callback(callbackResults);
      setGridItems([]);
    }
  };

  /**
   * Defined a function to process cells before they are copied.
   * Prevent copying the 'View' column (colId is `id`) by returning null or an empty string.
   * For all other columns, return the original value.
   */
  const processCellForClipboardForAuditGrid = useCallback((params) => {
    if (params.column.getColId() === 'id' && params?.column?.getPinned() === 'right') {
      return '';
    }
    return params.value;
  }, []);

  return (
    <IonGrid paginationEnabled
      enableDateFilter={enableDateFilter}
      enableRangeSelection={enableRangeSelection}
      selectedRange={selectedRange}
      showCustomPaginationBar
      loadItems={loadItems}
      augmentItems={augmentItems}
      columnDefinitions={getColumnDefinitions(showDifferences)}
      pageSizes={PAGE_SIZES}
      checkboxesEnabled={false}
      viewSubGroups={false}
      rowMenusEnabled={rowMenusEnabled}
      multilineColumns
      getGridHeight={getGridHeight}
      paginationClassName={classes.paginationPadding}
      rowHeight={DEFAULT_ROW_HEIGHT}
      ctaProps={{
        onViewAuditChanges
      }}
      rowBuffer={250}
      columnComponents={{
        attributesRenderer   : AttributesCellRenderer,
        valuesRenderer       : ValuesRenderer,
        ctcOldValuesRenderer : CTCOldValuesRenderer,
        ctcNewValuesRenderer : CTCNewValuesRenderer,
        rowMenu              : AuditRowMenuEditor
      }}
      processCellForClipboard={processCellForClipboardForAuditGrid}
      onGridReady={onGridReady} />
  );
};

/**
 * @augmentItems
 */
export const augmentItems = (items: AuditRecordType[]): GridResult[] => {
  const useUserLocaleDateFormat = getGlobalProp('date-time.formatter.useUserLocale', true) as boolean;
  return items?.map((item: AuditRecordType) => {
    const augmentedItem = {
      ...item,
      createdOnUI : formatUserDateTime(item?.date, !useUserLocaleDateFormat),
      user        : item?.username,
      attributes  : item?.attributeType?.split(DELIMITER),
      values      : item?.attributeValue?.split(DELIMITER),
      oldValues   : item?.oldValues,
      newValues   : item?.newValues
    };
    return augmentedItem;
  });
};

/**
 * @getColumnDefinitionsDesktop
 *
 * Returns column definitions for desktop audit grid
 */

const getColumnDefinitionsDesktop = (showDifferences: boolean): ColDef[] => {
  const columnDefinitions = [{
    headerName : translate('common:editors.audit.grid.columns.date'),
    field      : 'createdOnUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('common:editors.audit.grid.columns.user'),
    field      : 'user',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName         : translate(`common:editors.audit.grid.columns.${showDifferences ? 'attribute' : 'attributes'}`),
    field              : 'attributes',
    cellRenderer       : 'attributesRenderer',
    cellRendererParams : ({data}) => ({data}),
    autoHeight         : true,
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false,
    flex               : null,
    tooltipField       : null
  }];

  if (showDifferences) {
    columnDefinitions.push(
      {
        headerName         : translate('common:editors.audit.grid.columns.oldValue'),
        field              : 'oldValues',
        cellRenderer       : 'ctcOldValuesRenderer',
        cellRendererParams : ({data}) => ({data}),
        autoHeight         : true,
        sortable           : false,
        filter             : false,
        resizable          : true,
        unSortIcon         : false,
        hide               : false,
        flex               : null,
        tooltipField       : null
      }, {
        headerName         : translate('common:editors.audit.grid.columns.newValue'),
        field              : 'newValues',
        cellRenderer       : 'ctcNewValuesRenderer',
        cellRendererParams : ({data}) => ({data}),
        autoHeight         : true,
        sortable           : false,
        filter             : false,
        resizable          : true,
        unSortIcon         : false,
        hide               : false,
        flex               : 1,
        tooltipField       : null
      }
    );
  } else {
    columnDefinitions.push(
      {
        headerName         : translate('common:editors.audit.grid.columns.values'),
        field              : 'values',
        cellRenderer       : 'valuesRenderer',
        cellRendererParams : ({data}) => ({data}),
        autoHeight         : true,
        sortable           : false,
        filter             : false,
        resizable          : true,
        unSortIcon         : false,
        hide               : false,
        flex               : 1,
        tooltipField       : 'values'
      });
  }
  return columnDefinitions;
};

/**
 * @getColumnDefinitionsMobile
 *
 * Returns column definitions for mobile audit grid
 */
const getColumnDefinitionsMobile = (showDifferences: boolean): ColDef[] => {
  const columnDefinitions = [{
    headerName : translate('common:editors.audit.grid.columns.date'),
    field      : 'createdOnUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('common:editors.audit.grid.columns.user'),
    field      : 'user',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName         : translate('common:editors.audit.grid.columns.attributes'),
    field              : 'attributes',
    cellRenderer       : 'attributesRenderer',
    cellRendererParams : ({data}) => ({data}),
    autoHeight         : true,
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false,
    flex               : null,
    tooltipField       : null
  }];
  if (showDifferences) {
    columnDefinitions.push(
      {
        headerName         : translate('common:editors.audit.grid.columns.oldValue'),
        field              : 'oldValues',
        cellRenderer       : 'ctcOldValuesRenderer',
        cellRendererParams : ({data}) => ({data}),
        autoHeight         : true,
        sortable           : false,
        filter             : false,
        resizable          : true,
        unSortIcon         : false,
        hide               : false,
        flex               : null,
        tooltipField       : null
      }, {
        headerName         : translate('common:editors.audit.grid.columns.newValue'),
        field              : 'newValues',
        cellRenderer       : 'ctcNewValuesRenderer',
        cellRendererParams : ({data}) => ({data}),
        autoHeight         : true,
        sortable           : false,
        filter             : false,
        resizable          : true,
        unSortIcon         : false,
        hide               : false,
        flex               : 1,
        tooltipField       : null
      }
    );
  } else {
    columnDefinitions.push(
      {
        headerName         : translate('common:editors.audit.grid.columns.values'),
        field              : 'values',
        cellRenderer       : 'valuesRenderer',
        cellRendererParams : ({data}) => ({data}),
        autoHeight         : true,
        sortable           : false,
        filter             : false,
        resizable          : true,
        unSortIcon         : false,
        hide               : false,
        flex               : 1,
        tooltipField       : 'values'
      }
    );
  }
  return columnDefinitions;
};

const getColumnDefinitions = (showDifferences: boolean) => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop(showDifferences)),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile(showDifferences))
});

export default AuditGrid;
